import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { canWriteAccountingSettings, canWriteStammdaten, isAppAdmin } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { StammdatenTabNav } from "@/pages/stammdaten/StammdatenTabNav";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import { CheckoutSection } from "@/pages/stammdaten/settings/CheckoutSection";
import { DepositSection } from "@/pages/stammdaten/settings/DepositSection";
import { DevToolsSection } from "@/pages/stammdaten/settings/DevToolsSection";
import { devToolsVisibility } from "@/pages/stammdaten/settings/devToolsVisibility";
import { ElectricityBillingSection } from "@/pages/stammdaten/settings/ElectricityBillingSection";
import { GuestTypeDefaultsSection } from "@/pages/stammdaten/settings/GuestTypeDefaultsSection";
import { OccupancySection } from "@/pages/stammdaten/settings/OccupancySection";
import { PaymentTermsSection } from "@/pages/stammdaten/settings/PaymentTermsSection";
import { PricingPolicySection } from "@/pages/stammdaten/settings/PricingPolicySection";
import { TouristPaymentSection } from "@/pages/stammdaten/settings/TouristPaymentSection";
import {
  SETTINGS_TABS,
  parseSettingsTab,
  type SettingsTab,
} from "@/pages/stammdaten/settings/settingsSections";
import type { TenantSettings } from "@/pages/stammdaten/settings/tenantSettingsTypes";

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canWriteAdmin = canWriteStammdaten(groups);
  const canUseDevTools = canWriteAdmin || isAppAdmin(groups);
  const canWriteFinance = canWriteAccountingSettings(groups);

  const showDevTools = devToolsVisibility().any;
  const tabs = useMemo(
    () => SETTINGS_TABS.filter((tab) => tab.id !== "testsystem" || showDevTools),
    [showDevTools],
  );
  const requestedTab = parseSettingsTab(searchParams.get("tab"));
  const tab: SettingsTab = tabs.some((t) => t.id === requestedTab) ? requestedTab : "buchung";
  const activeTab = tabs.find((t) => t.id === tab) ?? tabs[0]!;

  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    api
      .get<TenantSettings>("/api/v1/settings")
      .then((data) => setSettings(data))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPageMeta({
      title: "Stammdaten",
      subtitle: `Buchungs- & Zahlungsregeln · ${activeTab.label}`,
    });
    return () => setPageMeta(null);
  }, [activeTab.label, setPageMeta]);

  useEffect(() => {
    load();
  }, [load]);

  function onTab(next: SettingsTab) {
    setSearchParams(next === "buchung" ? {} : { tab: next });
  }

  const tabCanWrite = tab === "zahlung" || tab === "strom" ? canWriteFinance : canWriteAdmin;
  const sectionProps = settings ? { settings, canWrite: tabCanWrite, onSaved: load } : null;

  return (
    <div className="min-w-0 space-y-6">
      <StammdatenTabNav tabs={tabs} active={tab} onChange={onTab} ariaLabel="Einstellungs-Bereiche" />

      {!tabCanWrite && !(tab === "testsystem" && canUseDevTools) ? <StammdatenWriteHint /> : null}
      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      {tab === "testsystem" ? (
        <DevToolsSection canWrite={canUseDevTools} />
      ) : loading || !sectionProps ? (
        <p className="text-sm text-label-secondary">Einstellungen werden geladen…</p>
      ) : tab === "buchung" ? (
        <>
          <CheckoutSection {...sectionProps} />
          <GuestTypeDefaultsSection {...sectionProps} />
          <OccupancySection {...sectionProps} />
        </>
      ) : tab === "zahlung" ? (
        <>
          <DepositSection {...sectionProps} />
          <PricingPolicySection {...sectionProps} />
          <PaymentTermsSection {...sectionProps} />
          <TouristPaymentSection {...sectionProps} />
        </>
      ) : (
        <ElectricityBillingSection {...sectionProps} />
      )}
    </div>
  );
}
